import { memo, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Activity, ChevronDown, ChevronRight, Wand2, Settings2 } from 'lucide-react';

// Per-algorithm hyperparameter schemas
const HYPERPARAM_SCHEMA: Record<string, Array<{
    key: string; label: string; type: 'number' | 'select' | 'boolean'; default: any;
    options?: { value: string; label: string }[]; min?: number; max?: number; step?: number;
}>> = {
    random_forest: [
        { key: 'n_estimators', label: 'Trees (n_estimators)', type: 'number', default: 100, min: 10, max: 1000, step: 10 },
        { key: 'max_depth', label: 'Max Depth (blank = unlimited)', type: 'number', default: '', min: 1, max: 100, step: 1 },
        { key: 'min_samples_split', label: 'Min Samples Split', type: 'number', default: 2, min: 2, max: 20, step: 1 },
        { key: 'min_samples_leaf', label: 'Min Samples Leaf', type: 'number', default: 1, min: 1, max: 20, step: 1 },
    ],
    logistic_regression: [
        { key: 'C', label: 'Regularization (C)', type: 'number', default: 1.0, min: 0.001, max: 1000, step: 0.1 },
        { key: 'solver', label: 'Solver', type: 'select', default: 'lbfgs', options: [
            { value: 'lbfgs', label: 'lbfgs (default)' },
            { value: 'liblinear', label: 'liblinear (small data)' },
            { value: 'saga', label: 'saga (large data)' },
            { value: 'newton-cg', label: 'newton-cg' },
        ]},
        { key: 'max_iter', label: 'Max Iterations', type: 'number', default: 1000, min: 100, max: 10000, step: 100 },
    ],
    catboost_classifier: [
        { key: 'iterations', label: 'Iterations', type: 'number', default: 100, min: 10, max: 2000, step: 10 },
        { key: 'depth', label: 'Tree Depth', type: 'number', default: 6, min: 1, max: 16, step: 1 },
        { key: 'learning_rate', label: 'Learning Rate', type: 'number', default: 0.1, min: 0.001, max: 1, step: 0.01 },
    ],
    linear_regression: [
        { key: 'fit_intercept', label: 'Fit Intercept', type: 'boolean', default: true },
    ],
    catboost_regressor: [
        { key: 'iterations', label: 'Iterations', type: 'number', default: 100, min: 10, max: 2000, step: 10 },
        { key: 'depth', label: 'Tree Depth', type: 'number', default: 6, min: 1, max: 16, step: 1 },
        { key: 'learning_rate', label: 'Learning Rate', type: 'number', default: 0.1, min: 0.001, max: 1, step: 0.01 },
    ],
    isolation_forest: [
        { key: 'n_estimators', label: 'Trees (n_estimators)', type: 'number', default: 100, min: 10, max: 1000, step: 10 },
        { key: 'contamination', label: 'Contamination Fraction', type: 'number', default: 0.1, min: 0.01, max: 0.5, step: 0.01 },
        { key: 'max_samples', label: 'Max Samples', type: 'select', default: 'auto', options: [
            { value: 'auto', label: 'auto' },
            { value: '0.5', label: '50%' },
            { value: '0.75', label: '75%' },
        ]},
    ],
};

const AUTO_TUNE_GRIDS: Record<string, string[]> = {
    random_forest: ['n_estimators: [50,100,200]', 'max_depth: [None,5,10,20]', 'min_samples_split: [2,5]'],
    logistic_regression: ['C: [0.01,0.1,1,10]', 'solver: [lbfgs,liblinear]', 'max_iter: [500,1000]'],
    linear_regression: ['fit_intercept: [True,False]'],
    isolation_forest: ['n_estimators: [50,100,200]', 'contamination: [0.05,0.1,0.2]'],
    catboost_classifier: ['iterations: [50,100,200]', 'depth: [4,6,8]', 'learning_rate: [0.05,0.1,0.2]'],
    catboost_regressor: ['iterations: [50,100,200]', 'depth: [4,6,8]', 'learning_rate: [0.05,0.1,0.2]'],
};

export const TrainModelConfig = memo(({ data, onChange, inputColumns = [] }: any) => {
    const [showHyperparams, setShowHyperparams] = useState(false);

    const cols = Array.isArray(inputColumns) ? inputColumns : [];
    const colNames = cols.map((c: any) => (typeof c === 'string' ? c : c?.name || c)).filter(Boolean);
    const features = data.features || data.featureColumns || [];
    const taskType = data.taskType || 'classification';
    const algorithm = data.algorithm || (taskType === 'regression' ? 'linear_regression' : taskType === 'anomaly' ? 'isolation_forest' : 'logistic_regression');
    const autoTune = data.autoTune || false;
    const hyperparams = data.hyperparameters || {};

    const ALGORITHMS = {
        classification: [
            { value: 'logistic_regression', label: 'Logistic Regression' },
            { value: 'random_forest', label: 'Random Forest' },
            { value: 'catboost_classifier', label: 'CatBoost (Classifier)' },
        ],
        regression: [
            { value: 'linear_regression', label: 'Linear Regression' },
            { value: 'random_forest', label: 'Random Forest' },
            { value: 'catboost_regressor', label: 'CatBoost (Regressor)' },
        ],
        anomaly: [
            { value: 'isolation_forest', label: 'Isolation Forest' },
        ],
    };

    const paramSchema = HYPERPARAM_SCHEMA[algorithm] || [];
    const autoTuneGrid = AUTO_TUNE_GRIDS[algorithm] || [];

    const updateHyperparam = (key: string, value: any) => {
        const next = { ...hyperparams, [key]: value };
        onChange('hyperparameters', next);
    };

    return (
        <div className="space-y-6">
            <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg text-xs text-indigo-800">
                <h4 className="font-semibold mb-1 flex items-center gap-1"><Activity className="h-3 w-3" /> Train Model</h4>
                <p>Train a scikit-learn model on your data. Supports classification, regression, and anomaly detection. Model is saved to the registry for reuse in Score/Predict nodes.</p>
            </div>

            {/* Task Type */}
            <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Task Type</Label>
                <Select value={taskType} onValueChange={(v) => {
                    onChange('taskType', v);
                    // Reset algorithm to default for new task type
                    const defaults: Record<string, string> = { classification: 'logistic_regression', regression: 'linear_regression', anomaly: 'isolation_forest' };
                    onChange('algorithm', defaults[v] || 'logistic_regression');
                    onChange('hyperparameters', {});
                }}>
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder="Select task" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg z-[100]">
                        <SelectItem value="classification">Classification (Predict Category)</SelectItem>
                        <SelectItem value="regression">Regression (Predict Number)</SelectItem>
                        <SelectItem value="anomaly">Anomaly Detection</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Algorithm */}
            <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Algorithm</Label>
                <Select value={algorithm} onValueChange={(v) => {
                    onChange('algorithm', v);
                    onChange('hyperparameters', {});
                }}>
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder="Select algorithm" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg z-[100]">
                        {(ALGORITHMS[taskType as keyof typeof ALGORITHMS] || ALGORITHMS.classification)?.map((a) => (
                            <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Feature Columns */}
            <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Feature Columns</Label>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-2 space-y-1">
                    <div className="flex justify-between px-1 pb-1 border-b border-gray-200 mb-1">
                        <button type="button" className="text-[10px] text-indigo-600 hover:underline" onClick={() => {
                            const all = colNames.filter((c: string) => c !== data.labelColumn);
                            onChange('features', all);
                            onChange('featureColumns', all);
                        }}>Select All</button>
                        <button type="button" className="text-[10px] text-gray-400 hover:text-gray-600" onClick={() => {
                            onChange('features', []);
                            onChange('featureColumns', []);
                        }}>Clear</button>
                    </div>
                    {colNames.map((col: string) => {
                        const isChecked = features.includes(col);
                        const isLabel = col === data.labelColumn;
                        return (
                            <div key={col} className={`flex items-center space-x-2 py-0.5 ${isLabel ? 'opacity-40' : ''}`}>
                                <input
                                    type="checkbox"
                                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    checked={isChecked}
                                    disabled={isLabel}
                                    onChange={(e) => {
                                        const next = e.target.checked ? [...features, col] : features.filter((f: string) => f !== col);
                                        onChange('features', next);
                                        onChange('featureColumns', next);
                                    }}
                                />
                                <span className="text-sm text-gray-700 truncate" title={col}>{col}</span>
                                {isLabel && <span className="text-[9px] text-blue-600 font-bold">(Target)</span>}
                            </div>
                        );
                    })}
                    {colNames.length === 0 && <p className="text-[10px] text-gray-400 italic">Connect upstream data to see columns.</p>}
                </div>
                <p className="text-[10px] text-gray-500">Select columns as model inputs. Exclude target/ID columns.</p>
            </div>

            {/* Target Column */}
            {taskType !== 'anomaly' && (
                <div className="space-y-2">
                    <Label className="text-xs font-semibold text-gray-700 uppercase">Target Column (Label)</Label>
                    <Select value={data.labelColumn || '_none'} onValueChange={(v) => onChange('labelColumn', v === '_none' ? '' : v)}>
                        <SelectTrigger className="w-full text-sm">
                            <SelectValue placeholder="Select target column..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white border border-gray-200 shadow-lg z-[100]">
                            <SelectItem value="_none">Select target column...</SelectItem>
                            {colNames.map((c: string) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}

            {/* Train / Test Split + Seed */}
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                    <Label className="text-xs font-semibold text-gray-700 uppercase">Test Split</Label>
                    <Input
                        type="number" step={0.05} min={0.1} max={0.5} className="w-full text-sm"
                        value={data.trainTestSplit ?? 0.2}
                        onChange={(e) => onChange('trainTestSplit', parseFloat(e.target.value) || 0.2)}
                    />
                    <p className="text-[10px] text-gray-400">0.2 = 80/20 split</p>
                </div>
                <div className="space-y-1">
                    <Label className="text-xs font-semibold text-gray-700 uppercase">Random Seed</Label>
                    <Input
                        type="number" className="w-full text-sm"
                        value={data.randomSeed ?? 42}
                        onChange={(e) => onChange('randomSeed', parseInt(e.target.value) || 42)}
                    />
                </div>
            </div>

            {/* Hyperparameter Section */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                    type="button"
                    className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
                    onClick={() => setShowHyperparams(!showHyperparams)}
                >
                    <span className="flex items-center gap-1.5"><Settings2 className="h-3.5 w-3.5" /> Hyperparameters</span>
                    {showHyperparams ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </button>

                {showHyperparams && (
                    <div className="p-3 space-y-4">
                        {/* Auto-tune toggle */}
                        <div className="flex items-start gap-3 p-2.5 rounded-lg bg-fuchsia-50 border border-fuchsia-100">
                            <input
                                type="checkbox"
                                id="autoTune"
                                checked={autoTune}
                                onChange={(e) => onChange('autoTune', e.target.checked)}
                                className="mt-0.5 rounded border-gray-300 text-fuchsia-600 focus:ring-fuchsia-500"
                            />
                            <div>
                                <Label htmlFor="autoTune" className="text-xs font-semibold text-fuchsia-800 flex items-center gap-1 cursor-pointer">
                                    <Wand2 className="h-3 w-3" /> Auto Hyperparameter Tuning (GridSearchCV)
                                </Label>
                                <p className="text-[10px] text-fuchsia-700 mt-0.5">
                                    Automatically searches the best parameters using cross-validation. Slower but optimal.
                                </p>
                                {autoTune && (
                                    <div className="mt-2 space-y-1">
                                        <Label className="text-[10px] text-gray-500 uppercase">CV Folds</Label>
                                        <Input
                                            type="number" min={2} max={10} className="w-24 h-7 text-xs"
                                            value={data.cvFolds ?? 3}
                                            onChange={(e) => onChange('cvFolds', parseInt(e.target.value) || 3)}
                                        />
                                        {autoTuneGrid.length > 0 && (
                                            <div className="mt-1.5">
                                                <p className="text-[10px] text-gray-500 mb-1">Search grid:</p>
                                                {autoTuneGrid.map((g, i) => (
                                                    <div key={i} className="text-[10px] font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded mb-0.5">{g}</div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Manual hyperparameters (shown when autoTune is off) */}
                        {!autoTune && paramSchema.length > 0 && (
                            <div className="space-y-3">
                                <p className="text-[10px] text-gray-500 uppercase font-semibold">Manual Parameters</p>
                                {paramSchema.map((param) => (
                                    <div key={param.key} className="space-y-1">
                                        <Label className="text-xs text-gray-600">{param.label}</Label>
                                        {param.type === 'select' ? (
                                            <Select
                                                value={String(hyperparams[param.key] ?? param.default)}
                                                onValueChange={(v) => updateHyperparam(param.key, v)}
                                            >
                                                <SelectTrigger className="w-full text-xs h-8">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-white border border-gray-200 shadow-lg z-[100]">
                                                    {param.options?.map((o) => (
                                                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : param.type === 'boolean' ? (
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={hyperparams[param.key] !== undefined ? Boolean(hyperparams[param.key]) : Boolean(param.default)}
                                                    onChange={(e) => updateHyperparam(param.key, e.target.checked)}
                                                    className="rounded border-gray-300 text-indigo-600"
                                                />
                                                <span className="text-xs text-gray-600">Enabled</span>
                                            </div>
                                        ) : (
                                            <Input
                                                type="number"
                                                step={param.step}
                                                min={param.min}
                                                max={param.max}
                                                className="w-full text-sm h-8"
                                                placeholder={String(param.default)}
                                                value={hyperparams[param.key] !== undefined ? hyperparams[param.key] : ''}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    if (raw === '') {
                                                        // Allow blank (null/unlimited for max_depth)
                                                        const next = { ...hyperparams };
                                                        delete next[param.key];
                                                        onChange('hyperparameters', next);
                                                    } else {
                                                        updateHyperparam(param.key, parseFloat(raw));
                                                    }
                                                }}
                                            />
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="text-[10px] text-gray-400 hover:text-gray-600 hover:underline"
                                    onClick={() => onChange('hyperparameters', {})}
                                >
                                    Reset to defaults
                                </button>
                            </div>
                        )}

                        {!autoTune && paramSchema.length === 0 && (
                            <p className="text-[10px] text-gray-400 italic">No configurable hyperparameters for this algorithm.</p>
                        )}
                    </div>
                )}
            </div>

            {/* Model Name */}
            <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Model Name (Registry Key)</Label>
                <Input
                    className="w-full text-sm"
                    placeholder="e.g. IrisClassifier"
                    value={data.modelName || data.registerModelName || ''}
                    onChange={(e) => {
                        onChange('modelName', e.target.value);
                        onChange('registerModelName', e.target.value);
                    }}
                />
                <p className="text-[10px] text-gray-500">Unique name for this model. Used by Score/Predict nodes to load it.</p>
            </div>
        </div>
    );
});
TrainModelConfig.displayName = 'TrainModelConfig';
